import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CustomSelect from '../components/CustomSelect';

interface CareActivity {
  id: number;
  name: string;
  category: string;
  description: string;
  estimatedDuration: number;
}

interface CarePlanTask {
  carePlanId: number;
  careActivityId: number;
  scheduledTime: string | null;
  frequency: string;
  specialInstructions: string | null;
  activity: CareActivity;
  completed: boolean;
  completedAt?: string;
  completionNotes?: string;
  durationMinutes?: number;
  residentResponse?: string;
  assistanceLevel?: string;
}

interface Resident {
  id: number;
  name: string;
  age: number;
  photo?: string;
  diagnosis: string;
}

interface CarePlanItem {
  id: number;
  residentId: number;
  careActivityId: number;
  scheduledTime: string | null;
  frequency: string;
  specialInstructions: string | null;
  activity: CareActivity;
  isActive: boolean;
}

interface FluidIntakeRecord {
  id: number;
  residentId: number;
  intakeDate: string;
  intakeTime: string;
  drinkType: string;
  amountMl: number;
  notes: string | null;
  recordedBy: {
    id: number;
    name: string;
  };
}

const DailyCare: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [residents, setResidents] = useState<Resident[]>([]);
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [todaysTasks, setTodaysTasks] = useState<CarePlanTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CarePlanTask | null>(null);
  const [completionForm, setCompletionForm] = useState({
    durationMinutes: '',
    notes: '',
    residentResponse: 'positive',
    assistanceLevel: 'minimal'
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCarePlanModal, setShowCarePlanModal] = useState(false);
  const [allActivities, setAllActivities] = useState<CareActivity[]>([]);
  const [carePlan, setCarePlan] = useState<CarePlanItem[]>([]);
  const [newTaskForm, setNewTaskForm] = useState({
    careActivityId: '',
    scheduledTime: '',
    frequency: 'daily',
    specialInstructions: ''
  });

  const [showNewActivityForm, setShowNewActivityForm] = useState(false);
  const [newActivityForm, setNewActivityForm] = useState({
    name: '',
    category: 'personal_care',
    description: '',
    frequency: 'daily',
    estimatedDuration: '15'
  });
  const [creatingActivity, setCreatingActivity] = useState(false);

  // Fluid intake state
  const [fluidIntakeRecords, setFluidIntakeRecords] = useState<FluidIntakeRecord[]>([]);
  const [fluidIntakeTotal, setFluidIntakeTotal] = useState(0);
  const [showFluidModal, setShowFluidModal] = useState(false);
  const [fluidForm, setFluidForm] = useState({
    drinkType: 'Water',
    amountMl: '200',
    intakeTime: '',
    notes: ''
  });

  const DRINK_TYPES = [
    'Water',
    'Tea',
    'Coffee',
    'Juice',
    'Milk',
    'Squash',
    'Hot Chocolate',
    'Soup',
    'Smoothie',
    'Fortified Drink',
    'Other'
  ];

  const COMMON_AMOUNTS = [50, 100, 150, 200, 250, 300, 350, 400, 500];

  const fetchResidents = async () => {
    try {
      const response = await api.get('/residents');
      if (response.data.success) {
        setResidents(response.data.data.residents || []);
        if (response.data.data.residents.length > 0) {
          setSelectedResident(response.data.data.residents[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching residents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaysTasks = useCallback(async () => {
    if (!selectedResident) return;
    
    try {
      const response = await api.get(`/daily-care/residents/${selectedResident.id}/tasks/today`);
      if (response.data.success) {
        setTodaysTasks(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching today\'s tasks:', error);
    }
  }, [selectedResident]);

  const fetchAllActivities = async () => {
    try {
      const response = await api.get('/daily-care/activities');
      if (response.data.success) {
        setAllActivities(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchCarePlan = async () => {
    if (!selectedResident) return;
    try {
      const response = await api.get(`/daily-care/residents/${selectedResident.id}/care-plan`);
      if (response.data.success) {
        setCarePlan(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching care plan:', error);
    }
  };

  const fetchFluidIntake = useCallback(async () => {
    if (!selectedResident) return;
    try {
      const response = await api.get(`/daily-care/residents/${selectedResident.id}/fluid-intake`);
      if (response.data.success) {
        setFluidIntakeRecords(response.data.data || []);
        setFluidIntakeTotal(response.data.totalMl || 0);
      }
    } catch (error) {
      console.error('Error fetching fluid intake:', error);
    }
  }, [selectedResident]);

  const handleRecordFluidIntake = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedResident) return;

    try {
      const response = await api.post(`/daily-care/residents/${selectedResident.id}/fluid-intake`, {
        drinkType: fluidForm.drinkType,
        amountMl: parseInt(fluidForm.amountMl),
        intakeTime: fluidForm.intakeTime || undefined,
        notes: fluidForm.notes || undefined
      });

      if (response.data.success) {
        setFluidForm({
          drinkType: 'Water',
          amountMl: '200',
          intakeTime: '',
          notes: ''
        });
        setShowFluidModal(false);
        fetchFluidIntake();
      }
    } catch (error: any) {
      console.error('Error recording fluid intake:', error);
      alert(error.response?.data?.message || 'Failed to record fluid intake');
    }
  };

  const handleDeleteFluidRecord = async (recordId: number) => {
    if (!window.confirm('Are you sure you want to delete this fluid intake record?')) return;
    
    try {
      await api.delete(`/daily-care/fluid-intake/${recordId}`);
      fetchFluidIntake();
    } catch (error: any) {
      console.error('Error deleting fluid intake:', error);
      alert(error.response?.data?.message || 'Failed to delete fluid intake record');
    }
  };

  const handleAddToCarePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedResident || !newTaskForm.careActivityId) {
      alert('Please select a resident and an activity');
      return;
    }

    try {
      const payload = {
        careActivityId: parseInt(newTaskForm.careActivityId),
        scheduledTime: newTaskForm.scheduledTime || null,
        frequency: newTaskForm.frequency,
        specialInstructions: newTaskForm.specialInstructions || null
      };
      
      const response = await api.post(`/daily-care/residents/${selectedResident.id}/care-plan`, payload);
      
      if (response.data.success) {
        setNewTaskForm({
          careActivityId: '',
          scheduledTime: '',
          frequency: 'daily',
          specialInstructions: ''
        });
        fetchCarePlan();
        fetchTodaysTasks();
        alert('Activity added to list of daily care successfully!');
      }
    } catch (error: any) {
      console.error('Error adding to list of daily care:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add activity to list of daily care';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleRemoveFromCarePlan = async (carePlanId: number) => {
    if (!window.confirm('Are you sure you want to remove this activity from the care plan?')) return;
    
    try {
      await api.delete(`/daily-care/care-plan/${carePlanId}`);
      fetchCarePlan();
      fetchTodaysTasks();
      alert('Activity removed from care plan successfully!');
    } catch (error: any) {
      console.error('Error removing from care plan:', error);
      alert(error.response?.data?.message || 'Failed to remove activity from care plan');
    }
  };

  const openCarePlanModal = () => {
    fetchAllActivities();
    fetchCarePlan();
    setShowCarePlanModal(true);
  };

  useEffect(() => {
    fetchResidents();
    fetchAllActivities();
  }, []);

  const handleCreateActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newActivityForm.name.trim() || !newActivityForm.category) {
      alert('Name and category are required');
      return;
    }
    setCreatingActivity(true);
    try {
      const response = await api.post('/daily-care/activities', {
        name: newActivityForm.name.trim(),
        category: newActivityForm.category,
        description: newActivityForm.description.trim() || undefined,
        frequency: newActivityForm.frequency,
        estimatedDuration: parseInt(newActivityForm.estimatedDuration) || 15
      });
      if (response.data.success) {
        await fetchAllActivities();
        const created = response.data.data;
        setNewTaskForm(f => ({ ...f, careActivityId: String(created.id) }));
        setNewActivityForm({
          name: '',
          category: 'personal_care',
          description: '',
          frequency: 'daily',
          estimatedDuration: '15'
        });
        setShowNewActivityForm(false);
      }
    } catch (error: any) {
      console.error('Error creating activity:', error);
      alert(error.response?.data?.message || 'Failed to create activity');
    } finally {
      setCreatingActivity(false);
    }
  };

  useEffect(() => {
    if (selectedResident) {
      fetchTodaysTasks();
      fetchFluidIntake();
    }
  }, [selectedResident, fetchTodaysTasks, fetchFluidIntake]);

  const handleCompleteTask = (task: CarePlanTask) => {
    setSelectedTask(task);
    setCompletionForm({
      durationMinutes: task.activity.estimatedDuration.toString(),
      notes: '',
      residentResponse: 'positive',
      assistanceLevel: 'minimal'
    });
    setShowCompleteModal(true);
  };

  const submitTaskCompletion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      await api.post('/daily-care/tasks/complete', {
        carePlanId: selectedTask.carePlanId,
        taskDate: today,
        durationMinutes: parseInt(completionForm.durationMinutes) || null,
        notes: completionForm.notes,
        residentResponse: completionForm.residentResponse,
        assistanceLevel: completionForm.assistanceLevel
      });

      setShowCompleteModal(false);
      setSelectedTask(null);
      fetchTodaysTasks();
    } catch (error: any) {
      console.error('Error completing task:', error);
      alert(error.response?.data?.message || 'Failed to complete task');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      personal_care: 'bg-blue-100 text-blue-800',
      food: 'bg-green-100 text-green-800',
      activities: 'bg-primary-100 text-primary-800',
      observations: 'bg-slate-100 text-slate-800',
      behaviour: 'bg-orange-100 text-orange-800',
      mood: 'bg-pink-100 text-pink-800',
      day_checks: 'bg-teal-100 text-teal-800',
      night_checks: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      personal_care: 'Personal Care',
      food: 'Food',
      activities: 'Activities',
      observations: 'Observations',
      behaviour: 'Behaviour',
      mood: 'Mood',
      day_checks: 'Day Checks',
      night_checks: 'Night Checks'
    };
    return labels[category] || category.replace('_', ' ');
  };

  const getCompletionPercentage = () => {
    if (todaysTasks.length === 0) return 0;
    const completed = todaysTasks.filter(t => t.completed).length;
    return Math.round((completed / todaysTasks.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading daily care tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Daily Care Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage daily care activities for residents
          </p>
        </div>
        {selectedResident && (
          <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
            {isAdmin && (
              <button
                onClick={openCarePlanModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Manage Daily Care
              </button>
            )}
            <button
              onClick={() => navigate(`/diary/${selectedResident.id}`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Add Note to Diary
            </button>
          </div>
        )}
      </div>

      {/* Resident Selection */}
      <div className="bg-white card-list p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Resident
        </label>
        <CustomSelect
          value={selectedResident?.id ? String(selectedResident.id) : ''}
          onChange={(v) => {
            const resident = residents.find(r => r.id === parseInt(v));
            setSelectedResident(resident || null);
          }}
          placeholder="Select a resident..."
          options={residents.map(resident => ({
            value: String(resident.id),
            label: `${resident.name} - ${resident.age} years old`,
          }))}
        />
      </div>

      {selectedResident && (
        <>
          {/* Progress Overview */}
          <div className="bg-white card-list p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Today's Progress</h3>
                <p className="text-sm text-gray-500">
                  {todaysTasks.filter(t => t.completed).length} of {todaysTasks.length} tasks completed
                </p>
              </div>
              <div className="text-3xl font-bold text-primary-600">
                {getCompletionPercentage()}%
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-primary-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${getCompletionPercentage()}%` }}
              ></div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="bg-white card-list p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Category
            </label>
            <CustomSelect
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={[
                { value: 'all', label: 'All Categories' },
                ...Array.from(new Set(allActivities.map(a => a.category)))
                  .sort()
                  .map(cat => ({ value: cat, label: getCategoryLabel(cat) }))
              ]}
            />
          </div>

          {/* Care Tasks */}
          <div className="bg-white card-list overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedCategory === 'all' ? "Today's Care Tasks" : `${getCategoryLabel(selectedCategory)} Tasks`}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {(() => {
                const filteredTasks = selectedCategory === 'all' 
                  ? todaysTasks 
                  : todaysTasks.filter(task => task.activity.category === selectedCategory);

                if (filteredTasks.length === 0) {
                  return (
                    <div className="px-6 py-12 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500">
                        {todaysTasks.length === 0 
                          ? "No care tasks scheduled for this resident today."
                          : `No ${getCategoryLabel(selectedCategory)} tasks scheduled for today.`
                        }
                      </p>
                      {isAdmin && todaysTasks.length === 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                          Add tasks to the list of daily care to get started.
                        </p>
                      )}
                    </div>
                  );
                }

                return filteredTasks.map((task) => (
                  <div key={`${task.carePlanId}-${task.careActivityId}-${task.scheduledTime || 'unscheduled'}`} className={`px-6 py-4 ${task.completed ? 'bg-green-50' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`flex-shrink-0 h-10 w-10 rounded-full ${task.completed ? 'bg-green-500' : 'bg-gray-300'} flex items-center justify-center`}>
                            {task.completed ? (
                              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-gray-900">{task.activity.name}</h4>
                              <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${getCategoryColor(task.activity.category)}`}>
                                {getCategoryLabel(task.activity.category)}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">{task.activity.description}</p>
                            {task.scheduledTime && (
                              <p className="mt-1 text-xs text-gray-500">
                                <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Scheduled: {task.scheduledTime}
                              </p>
                            )}
                            {task.specialInstructions && (
                              <p className="mt-1 text-xs text-blue-600 italic">
                                Note: {task.specialInstructions}
                              </p>
                            )}
                            {task.completed && task.completionNotes && (
                              <p className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                                <strong>Completion notes:</strong> {task.completionNotes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {!task.completed ? (
                          <button
                            onClick={() => handleCompleteTask(task)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-base leading-4 font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Mark Complete
                          </button>
                        ) : (
                          <div className="text-sm text-green-600 font-medium">
                            ✓ Completed
                            {task.completedAt && (
                              <div className="text-xs text-gray-500">
                                {new Date(task.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Fluid Intake Section */}
          <div className="bg-white card-list overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  Fluid Intake Today
                </h3>
                <p className="text-sm text-gray-500">Track daily fluid consumption</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{fluidIntakeTotal} ml</div>
                  <div className="text-xs text-gray-500">Daily Total</div>
                </div>
                <button
                  onClick={() => {
                    setFluidForm({
                      drinkType: 'Water',
                      amountMl: '200',
                      intakeTime: new Date().toTimeString().slice(0, 5),
                      notes: ''
                    });
                    setShowFluidModal(true);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-base leading-4 font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Record Fluid
                </button>
              </div>
            </div>
            
            {/* Progress Bar for recommended intake (2000ml) */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Progress to recommended 2000ml</span>
                <span className="text-xs font-medium text-gray-700">{Math.min(100, Math.round((fluidIntakeTotal / 2000) * 100))}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    fluidIntakeTotal >= 2000 ? 'bg-green-500' : 
                    fluidIntakeTotal >= 1500 ? 'bg-blue-500' : 
                    fluidIntakeTotal >= 1000 ? 'bg-yellow-500' : 'bg-red-400'
                  }`}
                  style={{ width: `${Math.min(100, (fluidIntakeTotal / 2000) * 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {fluidIntakeRecords.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No fluid intake recorded today.</p>
                  <p className="text-xs text-gray-400">Click "Record Fluid" to add the first entry.</p>
                </div>
              ) : (
                fluidIntakeRecords.map((record) => (
                  <div key={record.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{record.drinkType}</span>
                          <span className="text-sm font-bold text-blue-600">{record.amountMl}ml</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{record.intakeTime}</span>
                          <span>•</span>
                          <span>by {record.recordedBy?.name || 'Unknown'}</span>
                        </div>
                        {record.notes && (
                          <p className="text-xs text-gray-500 italic mt-1">{record.notes}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteFluidRecord(record.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete record"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Fluid Intake Modal */}
      {showFluidModal && selectedResident && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowFluidModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowFluidModal(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Record Fluid Intake
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    For {selectedResident.name}
                  </p>

                  <form onSubmit={handleRecordFluidIntake} className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Drink Type</label>
                      <div className="mt-1">
                        <CustomSelect
                          value={fluidForm.drinkType}
                          onChange={(v) => setFluidForm({...fluidForm, drinkType: v})}
                          options={DRINK_TYPES.map(type => ({ value: type, label: type }))}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Amount (ml)</label>
                      <div className="mt-1 flex flex-wrap gap-2 mb-2">
                        {COMMON_AMOUNTS.map(amount => (
                          <button
                            key={amount}
                            type="button"
                            onClick={() => setFluidForm({...fluidForm, amountMl: amount.toString()})}
                            className={`px-3 py-1 text-sm rounded-full border ${
                              fluidForm.amountMl === amount.toString()
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {amount}ml
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        value={fluidForm.amountMl}
                        onChange={(e) => setFluidForm({...fluidForm, amountMl: e.target.value})}
                        min="1"
                        max="2000"
                        required
                        className="block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter amount in ml"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Time</label>
                      <input
                        type="time"
                        value={fluidForm.intakeTime}
                        onChange={(e) => setFluidForm({...fluidForm, intakeTime: e.target.value})}
                        className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                      <textarea
                        value={fluidForm.notes}
                        onChange={(e) => setFluidForm({...fluidForm, notes: e.target.value})}
                        rows={2}
                        className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Any notes about fluid intake..."
                      />
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Record Intake
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFluidModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Care Plan Management Modal */}
      {showCarePlanModal && selectedResident && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCarePlanModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowCarePlanModal(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Manage Daily Care for {selectedResident.name}
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
                    {/* Add New Activity */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-gray-900">Add New Activity</h4>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => setShowNewActivityForm(s => !s)}
                            className="text-xs font-medium px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                          >
                            {showNewActivityForm ? 'Cancel' : '+ New Activity'}
                          </button>
                        )}
                      </div>

                      <form onSubmit={handleAddToCarePlan} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Select Activity</label>
                          <div className="mt-1">
                            <CustomSelect
                              value={newTaskForm.careActivityId}
                              onChange={(v) => setNewTaskForm({...newTaskForm, careActivityId: v})}
                              placeholder="Choose an activity..."
                              options={[
                                { value: '', label: 'Choose an activity...' },
                                ...Object.entries(
                                  allActivities.reduce((acc, activity) => {
                                    if (!acc[activity.category]) acc[activity.category] = [];
                                    acc[activity.category].push(activity);
                                    return acc;
                                  }, {} as Record<string, CareActivity[]>)
                                ).flatMap(([category, activities]) =>
                                  activities.map(activity => ({
                                    value: String(activity.id),
                                    label: `${getCategoryLabel(category)} — ${activity.name}`,
                                  }))
                                ),
                              ]}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Scheduled Time</label>
                          <input
                            type="time"
                            value={newTaskForm.scheduledTime}
                            onChange={(e) => setNewTaskForm({...newTaskForm, scheduledTime: e.target.value})}
                            className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Frequency</label>
                          <div className="mt-1">
                            <CustomSelect
                              value={newTaskForm.frequency}
                              onChange={(v) => setNewTaskForm({...newTaskForm, frequency: v})}
                              options={[
                                { value: 'daily', label: 'Daily' },
                                { value: 'twice_daily', label: 'Twice Daily' },
                                { value: 'three_times_daily', label: 'Three Times Daily' },
                                { value: 'weekly', label: 'Weekly' },
                                { value: 'as_needed', label: 'As Needed' },
                              ]}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                          <textarea
                            value={newTaskForm.specialInstructions}
                            onChange={(e) => setNewTaskForm({...newTaskForm, specialInstructions: e.target.value})}
                            rows={3}
                            className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="Any special notes or instructions..."
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add to List of Daily Care
                        </button>
                      </form>
                    </div>

                    {/* List of Current Daily Care */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">List of Current Daily Care ({carePlan.length} Active)</h4>
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {carePlan.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No activities in daily care yet.</p>
                        ) : (
                          carePlan.map((item) => (
                            <div key={item.id} className="border border-gray-200 rounded-lg p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h5 className="text-sm font-medium text-gray-900">{item.activity.name}</h5>
                                    <span className={`inline-flex items-center px-2 py-0.5 badge-pill ${getCategoryColor(item.activity.category)}`}>
                                      {getCategoryLabel(item.activity.category)}
                                    </span>
                                  </div>
                                  {item.scheduledTime && (
                                    <p className="mt-1 text-xs text-gray-500">Time: {item.scheduledTime}</p>
                                  )}
                                  <p className="mt-1 text-xs text-gray-500">Frequency: {item.frequency}</p>
                                  {item.specialInstructions && (
                                    <p className="mt-1 text-xs text-blue-600 italic">{item.specialInstructions}</p>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleRemoveFromCarePlan(item.id)}
                                  className="ml-2 text-red-600 hover:text-red-800"
                                >
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Task Modal */}
      {showCompleteModal && selectedTask && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCompleteModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={submitTaskCompletion}>
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Complete Task: {selectedTask.activity.name}
                    </h3>
                    <div className="mt-4 space-y-4 text-left">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Duration (minutes)</label>
                        <input
                          type="number"
                          value={completionForm.durationMinutes}
                          onChange={(e) => setCompletionForm({...completionForm, durationMinutes: e.target.value})}
                          className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Resident Response</label>
                        <div className="mt-1">
                          <CustomSelect
                            value={completionForm.residentResponse}
                            onChange={(v) => setCompletionForm({...completionForm, residentResponse: v})}
                            options={[
                              { value: 'positive', label: 'Positive' },
                              { value: 'neutral', label: 'Neutral' },
                              { value: 'resistant', label: 'Resistant' },
                              { value: 'agitated', label: 'Agitated' },
                            ]}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Assistance Level</label>
                        <div className="mt-1">
                          <CustomSelect
                            value={completionForm.assistanceLevel}
                            onChange={(v) => setCompletionForm({...completionForm, assistanceLevel: v})}
                            options={[
                              { value: 'independent', label: 'Independent' },
                              { value: 'minimal', label: 'Minimal Assistance' },
                              { value: 'moderate', label: 'Moderate Assistance' },
                              { value: 'full', label: 'Full Assistance' },
                            ]}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          value={completionForm.notes}
                          onChange={(e) => setCompletionForm({...completionForm, notes: e.target.value})}
                          rows={3}
                          className="mt-1 block w-full input-mobile border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="Any observations or additional notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm"
                  >
                    Mark as Complete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCompleteModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* New Activity Modal */}
      {showNewActivityForm && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowNewActivityForm(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowNewActivityForm(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">New Activity</h3>

              <form
                onSubmit={handleCreateActivity}
                className="space-y-3"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Name</label>
                  <input
                    type="text"
                    value={newActivityForm.name}
                    onChange={e => setNewActivityForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Morning Shower"
                    className="w-full input-mobile border border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <CustomSelect
                    value={newActivityForm.category}
                    onChange={(v) => setNewActivityForm(f => ({ ...f, category: v }))}
                    options={[
                      { value: 'personal_care', label: 'Personal Care' },
                      { value: 'food', label: 'Food' },
                      { value: 'activities', label: 'Activities' },
                      { value: 'observations', label: 'Observations' },
                      { value: 'behaviour', label: 'Behaviour' },
                      { value: 'mood', label: 'Mood' },
                      { value: 'day_checks', label: 'Day Checks' },
                      { value: 'night_checks', label: 'Night Checks' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newActivityForm.description}
                    onChange={e => setNewActivityForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description…"
                    rows={4}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
                    style={{ borderWidth: '1.5px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Duration (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    value={newActivityForm.estimatedDuration}
                    onChange={e => setNewActivityForm(f => ({ ...f, estimatedDuration: e.target.value }))}
                    placeholder="15"
                    className="w-full input-mobile border border-gray-300"
                  />
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={creatingActivity || !newActivityForm.name.trim()}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:col-start-2 sm:text-sm"
                  >
                    {creatingActivity ? 'Creating…' : 'Create Activity'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewActivityForm(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyCare;
